import { useState, useRef, useEffect, createElement } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useUIStore } from '@/stores/ui-store';

type TagName = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'div';

interface QuickEditInlineProps {
  value: string;
  onSave: (newValue: string) => Promise<unknown>;
  label: string;
  as?: TagName;
  className?: string;
  multiline?: boolean;
}

export default function QuickEditInline({
  value,
  onSave,
  label,
  as: Tag = 'span',
  className = '',
  multiline = false,
}: QuickEditInlineProps) {
  const { isActive } = useQuickEdit();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!isActive) {
    return createElement(Tag, { className }, value);
  }

  if (editing) {
    const InputTag = multiline ? 'textarea' : 'input';
    return (
      <span className="inline-flex items-center gap-1.5">
        <InputTag
          ref={inputRef as any}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setDraft(value);
              setEditing(false);
            }
            if (e.key === 'Enter' && !multiline) {
              e.preventDefault();
              handleSave();
            }
          }}
          className="rounded border border-blue-400 bg-white px-2 py-1 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ minWidth: '12rem' }}
          rows={multiline ? 3 : undefined}
          disabled={saving}
          aria-label={label}
        />
        <button
          onClick={handleSave}
          disabled={saving || draft === value}
          className="rounded bg-blue-600 p-1 text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          aria-label="Save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button
          onClick={() => { setDraft(value); setEditing(false); }}
          disabled={saving}
          className="rounded bg-gray-500 p-1 text-white transition-colors hover:bg-gray-600 disabled:opacity-50"
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <span
      className="group/inline relative inline cursor-pointer"
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') setEditing(true); }}
      aria-label={`Edit ${label}`}
    >
      {createElement(
        Tag,
        {
          className: `${className} decoration-blue-400 decoration-dashed underline-offset-4 group-hover/inline:underline`,
        },
        value,
      )}
      <Pencil className="ml-1 inline h-3 w-3 text-blue-400 hover-reveal hover-reveal-inline" />
    </span>
  );

  async function handleSave() {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err) {
      // Stay in edit mode so the typing is not lost, AND say something. This
      // used to be a bare catch: the field simply stayed open with no
      // explanation, which is indistinguishable from a slow save, so people
      // clicked away and lost the edit believing it had been written.
      useUIStore
        .getState()
        .addToast(
          err instanceof Error && err.message ? `Could not save: ${err.message}` : 'Could not save that change',
          'error',
        );
    } finally {
      setSaving(false);
    }
  }
}
